import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useGlobalContext } from "../../context/global";
import { getClientAccountDetails } from "../../helper/clientData/utils";
import removeDuplicateObjectsFromArray from "../../helper/common/removeDuplicateObjectsFromArray";

const AUTHORIZATION_HEADER = process.env.REACT_APP_AUTHORIZATION_FOR_NORMAL_USER;
const LOGIN_API_BASE = process.env.REACT_APP_LOGIN_API ?? "https://app1.adyogi.com";

const MAIN_TAB_ACCESS = "access";
const MAIN_TAB_MAPPING = "mapping";
const EMAIL_LEVEL = "email";
const CLIENT_LEVEL = "client";
const ACCESS_LEVEL_DEFAULT = "member";

const parseApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  const results = payload?.data?.results ?? payload?.results ?? payload?.data ?? null;
  if (Array.isArray(results)) return results;
  if (results && typeof results === "object") return [results];
  if (payload?.objectId) return [payload];
  return [];
};

const getEmailFromRecord = (item) => item?.user_email ?? item?.email ?? "-";
const isMultiUserEnabled = (value) => {
  const v = String(value ?? "")
    .trim()
    .toLowerCase();
  return v === "yes" || v === "true" || v === "1";
};

const getSort = (list, key) =>
  [...(list ?? [])].sort((a, b) => {
    const x = a?.[key]?.toLowerCase?.() ?? "";
    const y = b?.[key]?.toLowerCase?.() ?? "";
    if (x < y) return -1;
    if (x > y) return 1;
    return 0;
  });

export default function UserMapping() {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const { allClientsData, setAllClientsData } = useGlobalContext();
  const [searchParams] = useSearchParams();
  const showMultiUserOption = useMemo(
    () => isMultiUserEnabled(searchParams.get("multiUser")),
    [searchParams]
  );

  const [mainTab, setMainTab] = useState(MAIN_TAB_ACCESS);
  const [mappingLevel, setMappingLevel] = useState(EMAIL_LEVEL);

  const [isFetchingMappings, setIsFetchingMappings] = useState(false);
  const [isAccessLoading, setIsAccessLoading] = useState(false);
  const [isAccessUpdating, setIsAccessUpdating] = useState(false);

  const confirmActionRef = useRef(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    cancelLabel: "Cancel",
  });
  const [isConfirming, setIsConfirming] = useState(false);

  const openConfirmDialog = useCallback(
    ({
      title,
      message,
      onConfirm,
      confirmLabel = "Confirm",
      cancelLabel = "Cancel",
    }) => {
      confirmActionRef.current = onConfirm;
      setConfirmDialog({ open: true, title, message, confirmLabel, cancelLabel });
    },
    []
  );

  const closeConfirmDialog = useCallback(() => {
    confirmActionRef.current = null;
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const confirmDialogAction = useCallback(async () => {
    const action = confirmActionRef.current;
    if (!action) return;
    setIsConfirming(true);
    try {
      confirmActionRef.current = null;
      setConfirmDialog((prev) => ({ ...prev, open: false }));
      await action();
    } finally {
      setIsConfirming(false);
    }
  }, []);

  // User Access tab
  const [accessEmail, setAccessEmail] = useState("");
  const [accessDefaultClientId, setAccessDefaultClientId] = useState("");
  const [accessMultiUser, setAccessMultiUser] = useState(false);
  const [accessRecords, setAccessRecords] = useState([]);
  const [accessStepUserMappings, setAccessStepUserMappings] = useState([]);
  const [hasSearchedAccess, setHasSearchedAccess] = useState(false);

  // User Mapping tab
  const [emailQuery, setEmailQuery] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [emailMappings, setEmailMappings] = useState([]);
  const [emailDefaultAccessClientIds, setEmailDefaultAccessClientIds] = useState([]);
  const [clientMappings, setClientMappings] = useState([]);
  const [clientDefaultAccessEmails, setClientDefaultAccessEmails] = useState([]);
  const [excludeMultiUser, setExcludeMultiUser] = useState(true);
  const [hasSearchedEmail, setHasSearchedEmail] = useState(false);
  const [hasSearchedClient, setHasSearchedClient] = useState(false);
  const [clientListSearch, setClientListSearch] = useState("");

  const selectedClientOption = useMemo(
    () => (allClientsData ?? []).find((c) => c?.client_id === clientQuery) ?? null,
    [allClientsData, clientQuery]
  );

  const selectedDefaultClient = useMemo(
    () =>
      (allClientsData ?? []).find((c) => c?.client_id === accessDefaultClientId) ?? null,
    [allClientsData, accessDefaultClientId]
  );

  const clientIdToChipLabel = useMemo(() => {
    const map = new Map();
    (allClientsData ?? []).forEach((c) => {
      const cid = c?.client_id;
      if (!cid) return;
      const name = c?.name?.trim?.();
      map.set(cid, name ? `${name} (${cid})` : String(cid));
    });
    return map;
  }, [allClientsData]);

  const clientIdToName = useMemo(() => {
    const map = new Map();
    (allClientsData ?? []).forEach((c) => {
      const cid = c?.client_id;
      if (!cid) return;
      map.set(cid, c?.name ?? "");
    });
    return map;
  }, [allClientsData]);

  const primaryAccessRecord = useMemo(() => accessRecords?.[0] ?? null, [accessRecords]);
  const hasMappedDefaultClient = Boolean(
    primaryAccessRecord?.amzn_client_id
  );

  const mergedAccessContextClientIds = useMemo(() => {
    const ids = new Set();
    (accessRecords ?? []).forEach((rec) => {
      const id = rec?.amzn_client_id;
      if (id) ids.add(id);
    });
    (accessStepUserMappings ?? []).forEach((m) => {
      if (m?.parse_client_id) ids.add(m.parse_client_id);
    });
    return [...ids].sort();
  }, [accessRecords, accessStepUserMappings]);

  const fetchAllClients = useCallback(async () => {
    try {

      const getClientsFromMyntraAndFlipkart = async () => {
        const url = `${LOGIN_API_BASE}/api/B4APIs/getAllClients`;
        const config = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: AUTHORIZATION_HEADER,
          },
        };
        const data  = await axios.get(url, config);
        if (data?.status === 200) return data?.data;
        return data?.data ?? [];
      };

      const convertFlipkartAndMyntraData = (clientsData) =>
        clientsData?.reduce((acc, curr) => {
          const setupKey =
            curr?.flipkart_setup && curr?.flipkart_setup?.profile_id
              ? "flipkart_setup"
              : "myntra_setup";
          if (curr?.[setupKey] && curr?.[setupKey]?.profile_id) {
            const { accountName, accountType, profileId } = getClientAccountDetails(curr);
            acc.push({
              ...curr,
              name: accountName,
              account_id: profileId,
              client_id: curr?.objectId,
              account_type: accountType,
            });
          }
          return acc;
        }, []) ?? [];

      const mergeAmazon = (amazonData, flipKartData) => {
        return amazonData?.map((data) => {
          const sameIdData = flipKartData?.find((flipData) => {
            return data?.client_id === flipData?.client_id 
          }) ?? {}
          const flipkartData = (sameIdData['flipkart_setup'] && sameIdData['flipkart_setup']?.profileId) ? sameIdData['flipkart_setup'] : {}
          const isFlipKartDataPresent = Object.keys(flipkartData).length > 0
          const flipkart_setup = isFlipKartDataPresent ? flipkartData : {}
          const myntraData = (sameIdData['myntra_setup'] && sameIdData['myntra_setup']?.profileId) ? sameIdData['myntra_setup'] : {}
          const isMyntraDataPresent = Object.keys(myntraData)?.length > 0
          const myntra_setup = isMyntraDataPresent ? myntraData : {}
          return {...data,...(isFlipKartDataPresent && {flipkart_setup}),...(isMyntraDataPresent && {myntra_setup}),platform:'amazon'}
    
        })
      }
    
      const getAmazonClients = async () => {
        const { data } = await axios.get(
          "https://adyogiadsapi.adyogi.com/accounts?type=amazon_ad_account&fields=account_id,client_id,name,profile_id,account_type"
        );
        return data?.data ?? [];
      };

      const [amazonResponse, fkMyntraResponse] = await Promise.all([
        getAmazonClients(),
        getClientsFromMyntraAndFlipkart(),
      ]);

      const fkMyntra = convertFlipkartAndMyntraData(fkMyntraResponse);
      const amazonMerged = mergeAmazon(amazonResponse, fkMyntra);
      const overallData = removeDuplicateObjectsFromArray(
        (amazonMerged ?? []).concat(fkMyntra ?? []),
        true,
        "client_id"
      );
      setAllClientsData(getSort(overallData ?? [], "name"));
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch all clients");
    } finally {
      // no-op
    }
  }, [setAllClientsData]);

  useEffect(() => {
    fetchAllClients();
  }, [fetchAllClients]);

  const fetchMappingsByEmail = useCallback(async (emailOverride) => {
    const email = (
      typeof emailOverride === "string" ? emailOverride : emailQuery
    ).trim();
    if (!email) {
      toast.error("Please enter an email");
      return;
    }
    setIsFetchingMappings(true);
    try {
      const res = await fetch(
        `${LOGIN_API_BASE}/api/amzn/userMapping?email=${encodeURIComponent(email)}`,
        { headers: { Authorization: AUTHORIZATION_HEADER } }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || (json?.status && json.status !== 200)) {
        toast.error(json?.message ?? "Failed to fetch user mappings");
        setEmailMappings([]);
        setEmailDefaultAccessClientIds([]);
        return;
      }
      const mappingList = parseApiList(json);
      const accessRes = await fetch(
        `${LOGIN_API_BASE}/api/amzn/userAccess?email=${encodeURIComponent(email)}`,
        { headers: { Authorization: AUTHORIZATION_HEADER } }
      );
      const accessJson = await accessRes.json().catch(() => ({}));
      const accessList =
        accessRes.ok && (!accessJson?.status || accessJson.status === 200)
          ? parseApiList(accessJson)
          : [];
      const defaultClientIds = [
        ...new Set(
          accessList
            .map((row) => row?.amzn_client_id)
            .filter((clientId) => Boolean(clientId))
        ),
      ];
      setEmailDefaultAccessClientIds(defaultClientIds);

      const mergedByClientId = new Map();
      mappingList.forEach((row) => {
        const clientId = row?.parse_client_id;
        if (!clientId) return;
        mergedByClientId.set(clientId, {
          ...row,
          isDefaultAccessMapping: defaultClientIds.includes(clientId),
        });
      });
      defaultClientIds.forEach((clientId) => {
        if (mergedByClientId.has(clientId)) {
          const existing = mergedByClientId.get(clientId);
          mergedByClientId.set(clientId, {
            ...existing,
            isDefaultAccessMapping: true,
          });
          return;
        }
        mergedByClientId.set(clientId, {
          user_email: email,
          parse_client_id: clientId,
          access_level: ACCESS_LEVEL_DEFAULT,
          isDefaultAccessMapping: true,
        });
      });
      setEmailMappings([...mergedByClientId.values()]);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch user mappings");
      setEmailMappings([]);
      setEmailDefaultAccessClientIds([]);
    } finally {
      setIsFetchingMappings(false);
    }
  }, [emailQuery]);

  const fetchMappingsByClient = useCallback(async () => {
    const clientId = clientQuery.trim();
    if (!clientId) {
      toast.error("Please select a client");
      return;
    }
    setIsFetchingMappings(true);
    try {
      const res = await fetch(
        `${LOGIN_API_BASE}/api/amzn/userMapping?clientId=${encodeURIComponent(
          clientId
        )}`,
        { headers: { Authorization: AUTHORIZATION_HEADER } }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || (json?.status && json.status !== 200)) {
        toast.error(json?.message ?? "Failed to fetch user mappings");
        setClientMappings([]);
        setClientDefaultAccessEmails([]);
        return;
      }
      const mappingList = parseApiList(json);
      const accessQuery = new URLSearchParams({ clientId });
      if (!excludeMultiUser) {
        accessQuery.set("multiuser", "true");
      }
      const accessRes = await fetch(
        `${LOGIN_API_BASE}/api/amzn/userAccess?${accessQuery.toString()}`,
        { headers: { Authorization: AUTHORIZATION_HEADER } }
      );
      const accessJson = await accessRes.json().catch(() => ({}));
      const accessList =
        accessRes.ok && (!accessJson?.status || accessJson.status === 200)
          ? parseApiList(accessJson)
          : [];
      const multiUserEmails = new Set(
        accessList
          .filter((row) => isMultiUserEnabled(row?.multiuser ?? row?.multiUser))
          .map((row) => getEmailFromRecord(row))
          .filter((email) => Boolean(email && email !== "-"))
      );
      const defaultEmails = [
        ...new Set(
          accessList
            .map((row) => getEmailFromRecord(row))
            .filter((email) => Boolean(email && email !== "-"))
        ),
      ];
      setClientDefaultAccessEmails(defaultEmails);

      const mergedByEmail = new Map();
      mappingList.forEach((row) => {
        const email = row?.user_email;
        if (!email) return;
        mergedByEmail.set(email, {
          ...row,
          isDefaultAccessMapping: defaultEmails.includes(email),
          isMultiUser: isMultiUserEnabled(row?.multiuser ?? row?.multiUser),
        });
      });
      defaultEmails.forEach((email) => {
        if (mergedByEmail.has(email)) {
          const existing = mergedByEmail.get(email);
          mergedByEmail.set(email, {
            ...existing,
            isDefaultAccessMapping: true,
            isMultiUser:
              existing?.isMultiUser || multiUserEmails.has(email),
          });
          return;
        }
        mergedByEmail.set(email, {
          user_email: email,
          parse_client_id: clientId,
          access_level: ACCESS_LEVEL_DEFAULT,
          isDefaultAccessMapping: true,
          isMultiUser: multiUserEmails.has(email),
        });
      });
      setClientMappings([...mergedByEmail.values()]);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch user mappings");
      setClientMappings([]);
      setClientDefaultAccessEmails([]);
    } finally {
      setIsFetchingMappings(false);
    }
  }, [clientQuery, excludeMultiUser]);

  const addUserMapping = useCallback(async ({ user_email, parse_client_id, access_level }) => {
    const modifyEmail = localStorage.getItem("email") ?? "";
    const clientName = clientIdToName.get(parse_client_id) ?? "";
    const res = await fetch(`${LOGIN_API_BASE}/api/amzn/userMapping`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: AUTHORIZATION_HEADER,
      },
      body: JSON.stringify({
        user_email,
        parse_client_id,
        access_level,
        sendMail: true,
        clientName,
        modifyEmail,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || (json?.status && json.status !== 200 && json?.success !== true)) {
      throw new Error(json?.message ?? "Failed to add mapping");
    }
  }, [clientIdToName]);

  const deleteUserMapping = useCallback(
    async ({ objectId, user_email, parse_client_id, access_level }) => {
      const modifyEmail = localStorage.getItem("email") ?? "";
      const clientName = clientIdToName.get(parse_client_id) ?? "";
      const res = await fetch(`${LOGIN_API_BASE}/api/amzn/userMapping`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: AUTHORIZATION_HEADER,
        },
        body: JSON.stringify({
          objectId,
          user_email,
          parse_client_id,
          access_level,
          sendMail: true,
          clientName,
          modifyEmail,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || (json?.status && json.status !== 200 && json?.success !== true)) {
        throw new Error(json?.message ?? "Failed to delete mapping");
      }
    },
    [clientIdToName]
  );

  const fetchUserAccess = useCallback(async () => {
    const email = accessEmail.trim();
    if (!email) {
      toast.error("Please enter an email");
      return;
    }
    setIsAccessLoading(true);
    try {
      const headers = { Authorization: AUTHORIZATION_HEADER };
      const accessRes = await fetch(
        `${LOGIN_API_BASE}/api/amzn/userAccess?email=${encodeURIComponent(email)}`,
        { headers }
      );
      const accessJson = await accessRes.json().catch(() => ({}));

      if (!accessRes.ok || (accessJson?.status && accessJson.status !== 200)) {
        toast.error(accessJson?.message ?? "Failed to fetch user access");
        setAccessRecords([]);
        setAccessStepUserMappings([]);
        return;
      }
      const parsed = parseApiList(accessJson);
      let mappings = [];
      if (parsed.length === 0) {
        mappings = [];
      } else if (parsed.some((r) => r?.amzn_client_id)) {
        const mappingRes = await fetch(
          `${LOGIN_API_BASE}/api/amzn/userMapping?email=${encodeURIComponent(email)}`,
          { headers }
        );
        const mappingJson = await mappingRes.json().catch(() => ({}));
        if (mappingRes.ok && (!mappingJson?.status || mappingJson.status === 200)) {
          mappings = parseApiList(mappingJson);
        }
      } else {
        mappings = [];
      }
      // Commit access + mapping data together to avoid partial UI renders.
      setAccessRecords(parsed);
      setAccessStepUserMappings(mappings);

      const first = parsed?.[0];
      if (first?.amzn_client_id) {
        setAccessDefaultClientId(first?.amzn_client_id);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch user access");
      setAccessRecords([]);
      setAccessStepUserMappings([]);
    } finally {
      setIsAccessLoading(false);
    }
  }, [accessEmail]);

  const grantOrUpdateAccess = useCallback(async () => {
    const email = accessEmail.trim();
    if (!email) return toast.error("Please enter an email");
    if (!accessDefaultClientId) return toast.error("Please select default client");
    const modifyEmail = localStorage.getItem("email") ?? "";
    const clientName = clientIdToName.get(accessDefaultClientId) ?? "";
    setIsAccessUpdating(true);
    try {
      const res = await fetch(`${LOGIN_API_BASE}/api/amzn/userAccess`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: AUTHORIZATION_HEADER,
        },
        body: JSON.stringify({
          email,
          amzn_client_id: accessDefaultClientId,
          sendMail: true,
          clientName,
          modifyEmail,
          ...(showMultiUserOption && accessMultiUser ? { multi_user: true } : {}),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || (json?.status && json.status !== 200 && json?.success !== true)) {
        return toast.error(json?.message ?? "Failed to update access");
      }
      toast.success("User access updated");
      setHasSearchedAccess(true);
      await fetchUserAccess();
    } catch (error) {
      console.log(error);
      toast.error("Failed to update access");
    } finally {
      setIsAccessUpdating(false);
    }
  }, [
    accessDefaultClientId,
    accessEmail,
    accessMultiUser,
    clientIdToName,
    fetchUserAccess,
    showMultiUserOption,
  ]);

  const revokeAccess = useCallback(async () => {
    const email = accessEmail.trim();
    if (!email) return toast.error("Please enter an email");
    const modifyEmail = localStorage.getItem("email") ?? "";
    setIsAccessUpdating(true);
    try {
      const res = await fetch(`${LOGIN_API_BASE}/api/amzn/userAccess`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: AUTHORIZATION_HEADER,
        },
        body: JSON.stringify({
          email,
          sendMail: true,
          modifyEmail,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || (json?.status && json.status !== 200 && json?.success !== true)) {
        return toast.error(json?.message ?? "Failed to revoke access");
      }
      toast.success("User access revoked");
      setAccessRecords([]);
      setAccessStepUserMappings([]);
      setHasSearchedAccess(true);
    } catch (error) {
      console.log(error);
      toast.error("Failed to revoke access");
    } finally {
      setIsAccessUpdating(false);
    }
  }, [accessEmail]);

  const handleRemoveMapping = useCallback(
    async (userEmail, clientId, objectId, { isDefaultAccessMapping = false } = {}) => {
      if (isDefaultAccessMapping || emailDefaultAccessClientIds.includes(clientId)) {
        toast.error("Default account mapping cannot be removed from here");
        return;
      }
      const prevEmail = emailMappings;
      const prevClient = clientMappings;
      setEmailMappings((prev) =>
        prev.filter((m) => m?.parse_client_id !== clientId || m?.user_email !== userEmail)
      );
      setClientMappings((prev) =>
        prev.filter((m) => m?.parse_client_id !== clientId || m?.user_email !== userEmail)
      );
      try {
        await deleteUserMapping({
          objectId,
          user_email: userEmail,
          parse_client_id: clientId,
          access_level: ACCESS_LEVEL_DEFAULT,
        });
        toast.success("Mapping removed");
      } catch (error) {
        console.log(error);
        setEmailMappings(prevEmail);
        setClientMappings(prevClient);
        toast.error("Failed to remove mapping");
      }
    },
    [clientMappings, deleteUserMapping, emailDefaultAccessClientIds, emailMappings]
  );

  const handleAddClientToEmail = useCallback(
    async (userEmail, clientId) => {
      const prevEmail = emailMappings;
      const prevClient = clientMappings;
      if (
        prevEmail.some((m) => m?.parse_client_id === clientId && m?.user_email === userEmail)
      ) {
        return;
      }
      const optimistic = {
        user_email: userEmail,
        parse_client_id: clientId,
        access_level: ACCESS_LEVEL_DEFAULT,
      };
      setEmailMappings((prev) => [...prev, optimistic]);
      setClientMappings((prev) => [...prev, optimistic]);
      try {
        await addUserMapping(optimistic);
        toast.success("Mapping added");
      } catch (error) {
        console.log(error);
        setEmailMappings(prevEmail);
        setClientMappings(prevClient);
        toast.error("Failed to add mapping");
      }
    },
    [addUserMapping, clientMappings, emailMappings]
  );

  const emailMappedClients = useMemo(() => {
    const map = new Map();
    (emailMappings ?? []).forEach((m) => {
      if (!m?.parse_client_id) return;
      map.set(m.parse_client_id, m);
    });
    return map;
  }, [emailMappings]);

  const filteredEmailClients = useMemo(() => {
    const q = clientListSearch.trim().toLowerCase();
    if (!q) return allClientsData ?? [];
    return (allClientsData ?? []).filter((c) => {
      const name = (c?.name ?? "").toLowerCase();
      const id = (c?.client_id ?? "").toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [allClientsData, clientListSearch]);

  const emailSortedClients = useMemo(() => {
    const mapped = [];
    const unmapped = [];
    (filteredEmailClients ?? []).forEach((c) => {
      const id = c?.client_id;
      if (emailMappedClients.get(id)) mapped.push(c);
      else unmapped.push(c);
    });
    return [...getSort(mapped, "name"), ...getSort(unmapped, "name")];
  }, [emailMappedClients, filteredEmailClients]);

  const clientMappedEmails = useMemo(() => {
    if (!clientMappings?.length) return [];
    return removeDuplicateObjectsFromArray(clientMappings, true, "user_email");
  }, [clientMappings]);

  const onSearchMapping = useCallback(async () => {
    if (mappingLevel === EMAIL_LEVEL) {
      setHasSearchedEmail(true);
      await fetchMappingsByEmail();
      return;
    }
    setHasSearchedClient(true);
    await fetchMappingsByClient();
  }, [fetchMappingsByClient, fetchMappingsByEmail, mappingLevel]);

  const goToEmailMappingFromAccess = useCallback(async () => {
    const email =
      accessEmail.trim() || getEmailFromRecord(primaryAccessRecord).trim();
    if (!email || email === "-") {
      toast.error("No email available for mapping");
      return;
    }
    setMainTab(MAIN_TAB_MAPPING);
    setMappingLevel(EMAIL_LEVEL);
    setEmailQuery(email);
    setHasSearchedEmail(true);
    await fetchMappingsByEmail(email);
  }, [accessEmail, fetchMappingsByEmail, primaryAccessRecord]);

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box sx={{ width: "100%", mx: "auto", px: { xs: 1, sm: 2 } }}>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ m: 1 }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0 }}>
            User Access checks identity and default account. User Mapping manages
            user–client mapping by email or client level.
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Tabs
            value={mainTab}
            onChange={(_, v) => setMainTab(v)}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            sx={{ borderBottom: "1px solid rgba(224,224,224,1)" }}
          >
            <Tab value={MAIN_TAB_ACCESS} label="User Access" />
            <Tab value={MAIN_TAB_MAPPING} label="User Mapping" />
          </Tabs>

          <Box sx={{ p: 2.5 }}>
            {mainTab === MAIN_TAB_ACCESS ? (
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label="Search user by email"
                    value={accessEmail}
                    onChange={(e) => {
                      setAccessEmail(e.target.value);
                      setHasSearchedAccess(false);
                      setAccessRecords([]);
                      setAccessStepUserMappings([]);
                    }}
                    size="small"
                    fullWidth
                    placeholder="e.g. ankit.singh@adyogi.com"
                  />
                  <Button
                    variant="contained"
                    onClick={async () => {
                      setHasSearchedAccess(true);
                      await fetchUserAccess();
                    }}
                    disabled={isAccessLoading}
                    sx={{ height: 40, whiteSpace: "nowrap" }}
                  >
                    {isAccessLoading ? "Searching..." : "Search"}
                  </Button>
                </Stack>

                {!accessEmail.trim() ? (
                  <Alert severity="secondary">Enter email and click Search.</Alert>
                ) : isAccessLoading && accessRecords.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress />
                  </Box>
                ) : !hasSearchedAccess ? (
                  <Alert severity="info">Click Search to view identity details.</Alert>
                ) : accessRecords.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{ p: 3, borderStyle: "dashed", borderColor: "divider", borderRadius: 3 }}
                  >
                    <Stack spacing={2} alignItems="center" textAlign="center">
                      <Typography variant="h6" fontWeight={700}>
                        Identity Not Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The email <b>{accessEmail}</b> does not match any existing records.
                      </Typography>
                      <Autocomplete
                        options={allClientsData ?? []}
                        value={selectedDefaultClient}
                        onChange={(_, value) => setAccessDefaultClientId(value?.client_id ?? "")}
                        isOptionEqualToValue={(option, value) =>
                          option?.client_id === value?.client_id
                        }
                        getOptionLabel={(option) =>
                          option?.name
                            ? `${option.name} (${option.client_id})`
                            : option?.client_id ?? ""
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Default mapping client (amzn_client_id)"
                          />
                        )}
                        ListboxProps={{ style: { maxHeight: 350, overflow: "auto" } }}
                        sx={{ width: "100%", maxWidth: 560 }}
                      />
                      {showMultiUserOption ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={accessMultiUser}
                              onChange={(e) => setAccessMultiUser(e.target.checked)}
                            />
                          }
                          label="Multi User"
                        />
                      ) : null}
                      <Button
                        variant="contained"
                        size="large"
                        onClick={grantOrUpdateAccess}
                        disabled={isAccessUpdating}
                        sx={{ minWidth: 320 }}
                      >
                        {isAccessUpdating
                          ? "Creating..."
                          : "Create New Record"}
                      </Button>
                    </Stack>
                  </Paper>
                ) : !hasMappedDefaultClient ? (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          {getEmailFromRecord(primaryAccessRecord)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Multi User Access: {primaryAccessRecord?.multiuser ?? "No"}
                        </Typography>
                        <Chip
                          label="Inactive"
                          size="small"
                          color="warning"
                          sx={{ mt: 1 }}
                        />
                        {mergedAccessContextClientIds.length > 0 ? (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Mapped accounts & contexts
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {mergedAccessContextClientIds.map((id) => (
                                <Chip
                                  key={`access-ctx-${id}`}
                                  label={clientIdToChipLabel.get(id) ?? id}
                                  variant="outlined"
                                  size="small"
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        ) : null}
                      </Box>
                      <Box sx={{ width:"500px"}}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          No Default Account Linked
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          This user exists but has no marketplace default mapping.
                        </Typography>
                        <Autocomplete
                          options={allClientsData ?? []}
                          value={selectedDefaultClient}
                          onChange={(_, value) => setAccessDefaultClientId(value?.client_id ?? "")}
                          isOptionEqualToValue={(option, value) =>
                            option?.client_id === value?.client_id
                          }
                          getOptionLabel={(option) =>
                            option?.name
                              ? `${option.name} (${option.client_id})`
                              : option?.client_id ?? ""
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label="Select default mapping client"
                            />
                          )}
                          ListboxProps={{ style: { maxHeight: 350, overflow: "auto" } }}
                        />
                        {showMultiUserOption ? (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={accessMultiUser}
                                onChange={(e) => setAccessMultiUser(e.target.checked)}
                              />
                            }
                            label="Multi User"
                            sx={{ mt: 1 }}
                          />
                        ) : null}
                      </Box>
                      <Button
                        variant="contained"
                        onClick={grantOrUpdateAccess}
                        disabled={isAccessUpdating}
                      >
                        {isAccessUpdating ? "Adding..." : "Add Account Mapping"}
                      </Button>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          {getEmailFromRecord(primaryAccessRecord)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Multi User Access: {primaryAccessRecord?.multiuser ?? "No"}
                        </Typography>
                        <Chip label="Active" size="small" color="success" sx={{ mt: 1 }} />
                      </Box>
                      <Box sx={{ width:"500px"}}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Mapped accounts & contexts
                        </Typography>
                        <Stack direction="row" spacing={1} gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {mergedAccessContextClientIds.map((id) => (
                            <Chip
                              key={`access-ctx-${id}`}
                              label={clientIdToChipLabel.get(id) ?? id}
                              variant="filled"
                            />
                          ))}
                        </Stack>
                      </Box>
                      <Stack spacing={1}>
                        <Button variant="contained" onClick={goToEmailMappingFromAccess}>
                          Edit Mapping Details
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            const emailForDialog =
                              accessEmail.trim() ||
                              getEmailFromRecord(primaryAccessRecord).trim();
                            if (!emailForDialog || emailForDialog === "-") {
                              toast.error("No email available for revocation");
                              return;
                            }
                            openConfirmDialog({
                              title: "Revoke all access",
                              message: `Are you sure you want to revoke all access for ${emailForDialog}?`,
                              confirmLabel: "Revoke",
                              cancelLabel: "Cancel",
                              onConfirm: revokeAccess,
                            });
                          }}
                          disabled={isAccessUpdating}
                        >
                          {isAccessUpdating ? "Revoking..." : "Revoke All Access"}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Tabs
                  value={mappingLevel}
                  onChange={(_, v) => setMappingLevel(v)}
                  textColor="primary"
                  indicatorColor="primary"
                  variant="scrollable"
                  sx={{ borderBottom: "1px solid rgba(224,224,224,1)" }}
                >
                  <Tab value={EMAIL_LEVEL} label="Email level" />
                  <Tab value={CLIENT_LEVEL} label="Client level" />
                </Tabs>

                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flex: 1 }}>
                    {mappingLevel === EMAIL_LEVEL ? (
                      <TextField
                        label="User email"
                        value={emailQuery}
                        onChange={(e) => {
                          setEmailQuery(e.target.value);
                          setHasSearchedEmail(false);
                          setEmailMappings([]);
                          setEmailDefaultAccessClientIds([]);
                          setClientListSearch("");
                        }}
                        size="small"
                        fullWidth
                        placeholder="e.g. ankit.singh@adyogi.com"
                      />
                    ) : (
                      <Autocomplete
                        options={allClientsData ?? []}
                        value={selectedClientOption}
                        onChange={(_, newValue) => {
                          setClientQuery(newValue?.client_id ?? "");
                          setHasSearchedClient(false);
                          setClientMappings([]);
                          setClientDefaultAccessEmails([]);
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option?.client_id === value?.client_id
                        }
                        getOptionLabel={(option) =>
                          option?.name
                            ? `${option.name} (${option.client_id})`
                            : option?.client_id ?? ""
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select client"
                            size="small"
                            placeholder="Choose from clients"
                          />
                        )}
                        ListboxProps={{ style: { maxHeight: 350, overflow: "auto" } }}
                        clearOnEscape
                        fullWidth
                      />
                    )}
                    <Button
                      variant="contained"
                      onClick={onSearchMapping}
                      disabled={isFetchingMappings}
                      sx={{ height: 40, whiteSpace: "nowrap" }}
                    >
                      {isFetchingMappings ? "Searching..." : "Search"}
                    </Button>
                  </Stack>
                </Stack>
                {mappingLevel === CLIENT_LEVEL ? (
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={excludeMultiUser}
                          onChange={(e) => setExcludeMultiUser(e.target.checked)}
                        />
                      }
                      label="Don't include multi user"
                    />
                  </Box>
                ) : null}

                {mappingLevel === EMAIL_LEVEL ? (
                  <>
                    {!emailQuery.trim() ? (
                      <Alert severity="secondary">Enter an email and click Search.</Alert>
                    ) : !hasSearchedEmail ? (
                      <Alert severity="info">Click Search to view mapped clients.</Alert>
                    ) : isFetchingMappings ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        {emailMappings.length === 0 && (
                          <Alert severity="warning">
                            No mappings found for this email. You can add clients below.
                          </Alert>
                        )}
                        <TextField
                          label="Search clients (name or id)"
                          value={clientListSearch}
                          onChange={(e) => setClientListSearch(e.target.value)}
                          size="small"
                          fullWidth
                        />
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                                  Client name
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                                  Client id
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                                  Access level
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                                  Action
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {emailSortedClients.map((client) => {
                                const clientId = client?.client_id;
                                const mapped = emailMappedClients.get(clientId);
                                const isMapped = Boolean(mapped);
                                const isDefaultAccessMapping = Boolean(
                                  mapped?.isDefaultAccessMapping ||
                                    emailDefaultAccessClientIds.includes(clientId)
                                );
                                return (
                                  <TableRow key={clientId} hover>
                                    <TableCell>{client?.name ?? "-"}</TableCell>
                                    <TableCell>{clientId ?? "-"}</TableCell>
                                    <TableCell>{mapped?.access_level ?? "-"}</TableCell>
                                    <TableCell align="right">
                                      {isMapped ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            disabled={isDefaultAccessMapping}
                                            onClick={() => {
                                              const emailForDialog = emailQuery.trim();
                                              const clientLabel = client?.name ?? clientId ?? "-";
                                              openConfirmDialog({
                                                title: "Remove mapping",
                                                message: `Remove mapping for ${emailForDialog} on ${clientLabel}?`,
                                                confirmLabel: "Remove",
                                                cancelLabel: "Cancel",
                                                onConfirm: () =>
                                                  handleRemoveMapping(
                                                    emailForDialog,
                                                    clientId,
                                                    mapped?.objectId,
                                                    {
                                                      isDefaultAccessMapping,
                                                    }
                                                  ),
                                              });
                                            }}
                                          >
                                            {isDefaultAccessMapping ? "Default Mapping" : "Remove"}
                                          </Button>
                                          {isDefaultAccessMapping ? (
                                            <Tooltip title="Default Mapping is mandatory. If you want to delete this mapping, use Revoke All Access.">
                                              <IconButton size="small">
                                                <InfoOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          ) : null}
                                        </Box>
                                      ) : (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() =>
                                            handleAddClientToEmail(
                                              emailQuery.trim(),
                                              clientId
                                            )
                                          }
                                        >
                                          Add
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {!clientQuery.trim() ? (
                      <Alert severity="secondary">Select client and click Search.</Alert>
                    ) : !hasSearchedClient ? (
                      <Alert severity="info">Click Search to view mapped users.</Alert>
                    ) : isFetchingMappings ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                                User email
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                                Access level
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                                Action
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {clientMappedEmails.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3}>
                                  <Alert severity="warning">
                                    No mappings found for this client id.
                                  </Alert>
                                </TableCell>
                              </TableRow>
                            ) : (
                              clientMappings.map((m, idx) => (
                                <TableRow key={`${m?.objectId ?? "map"}_${idx}`}>
                                  <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <Typography variant="body2">{m?.user_email ?? "-"}</Typography>
                                      {isMultiUserEnabled(m?.multiuser ?? m?.multiUser) ||
                                      m?.isMultiUser ? (
                                        <Chip label="Multi User" size="small"  />
                                      ) : null}
                                    </Box>
                                  </TableCell>
                                  <TableCell>{m?.access_level ?? "-"}</TableCell>
                                  <TableCell align="right">
                                    {(() => {
                                      const isDefaultAccessMapping = Boolean(
                                        m?.isDefaultAccessMapping ||
                                          clientDefaultAccessEmails.includes(m?.user_email)
                                      );
                                      return (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            disabled={isDefaultAccessMapping}
                                            onClick={() => {
                                              const emailForDialog = m?.user_email ?? "-";
                                              const clientLabel = m?.parse_client_id ?? "-";
                                              openConfirmDialog({
                                                title: "Remove mapping",
                                                message: `Remove mapping for ${emailForDialog} on ${clientLabel}?`,
                                                confirmLabel: "Remove",
                                                cancelLabel: "Cancel",
                                                onConfirm: () =>
                                                  handleRemoveMapping(
                                                    m?.user_email,
                                                    m?.parse_client_id,
                                                    m?.objectId,
                                                    { isDefaultAccessMapping }
                                                  ),
                                              });
                                            }}
                                          >
                                            {isDefaultAccessMapping ? "Default Mapping" : "Remove"}
                                          </Button>
                                          {isDefaultAccessMapping ? (
                                            <Tooltip title="Default Mapping is mandatory. If you want to delete this mapping, use Revoke All Access.">
                                              <IconButton size="small">
                                                <InfoOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          ) : null}
                                        </Box>
                                      );
                                    })()}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent dividers>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={isConfirming}>
            {confirmDialog.cancelLabel}
          </Button>
          <Button
            onClick={confirmDialogAction}
            variant="contained"
            color="error"
            disabled={isConfirming}
          >
            {isConfirming ? "Please wait..." : confirmDialog.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
